/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Target, CheckCircle, XCircle, RotateCcw, Brain, Guitar, Music } from 'lucide-react';
import { FretPosition } from '../Fretboard';
import NoteRecognitionQuiz, { QuizBannerState } from './NoteRecognitionQuiz';
import ChordRecognitionQuiz from './ChordRecognitionQuiz';
import IntervalRecognitionQuiz from './IntervalRecognitionQuiz';

export type ExerciseType = 'noteRecognition' | 'chordRecognition' | 'intervalRecognition';

interface FretboardQuizProps {
  currentKey: string;
  currentScale: string;
  noteCollection: any;
  tuning: string[];
  // Fretboard control props
  fretboardInteractive: boolean;
  setFretboardInteractive: (active: boolean) => void;
  setRootNotePosition: (position: FretPosition | null) => void;
  userFretboardAnswers: FretPosition[];
  setUserFretboardAnswers: (answers: FretPosition[]) => void;
  setHighlightedFretPositions: (positions: FretPosition[]) => void;
  correctUserFretboardAnswers: FretPosition[];
  setCorrectUserFretboardAnswers: (positions: FretPosition[]) => void;
  incorrectUserFretboardAnswers: FretPosition[];
  setIncorrectUserFretboardAnswers: (positions: FretPosition[]) => void;
  fretRange?: { start: number; end: number };
  setFretRange?: (range: { start: number; end: number }) => void;
  isStandardTuning: boolean;
  // Test mode props
  testMode?: boolean;
  onScoreUpdate?: (correct: number, total: number) => void;
  /** When true, shows compact overlay banner instead of full Card UI */
  isMobile?: boolean;
  /** Callback to expose quiz state to parent for mobile banner rendering */
  onQuizStateChange?: (state: QuizBannerState) => void;
}

export interface QuizScore {
  correct: number;
  total: number;
  streak: number;
}

const FretboardQuiz: React.FC<FretboardQuizProps> = ({
  currentKey,
  currentScale,
  noteCollection,
  tuning,
  // Fretboard control props
  fretboardInteractive,
  setFretboardInteractive,
  setRootNotePosition,
  userFretboardAnswers,
  setUserFretboardAnswers,
  setHighlightedFretPositions,
  correctUserFretboardAnswers,
  setCorrectUserFretboardAnswers,
  incorrectUserFretboardAnswers,
  setIncorrectUserFretboardAnswers,
  fretRange = { start: 0, end: 12 },
  setFretRange,
  isStandardTuning,
  // Test mode props
  testMode = false,
  onScoreUpdate,
  isMobile = false,
  onQuizStateChange
}) => {
  const [exerciseType, setExerciseType] = useState<ExerciseType>('noteRecognition');
  const [score, setScore] = useState<QuizScore>({ correct: 0, total: 0, streak: 0 });

  // Exercise type options
  const exerciseOptions = [
    {
      value: 'noteRecognition',
      label: 'Note Recognition',
      description: 'Find specific notes on the fretboard',
      icon: Target,
      available: true
    },
    // {
    //   value: 'chordRecognition',
    //   label: 'Chord Recognition',
    //   description: 'Draw chord shapes and fingerings',
    //   icon: Guitar,
    //   available: isStandardTuning
    // },
    {
      value: 'intervalRecognition',
      label: 'Interval Recognition',
      description: 'Find intervals from root notes',
      icon: Music,
      available: true
    }
  ];

  // Update score from child components
  const updateScore = (correct: number, total: number, streak: number) => {
    setScore({ correct, total, streak });
    // Notify parent component in test mode
    if (testMode && onScoreUpdate) {
      onScoreUpdate(correct, total);
    }
  };

  // Reset score
  const resetScore = () => {
    setScore({ correct: 0, total: 0, streak: 0 });
  };

  // Handle exercise type change
  const handleExerciseTypeChange = (newType: ExerciseType) => {
    setExerciseType(newType);
    // Clear fretboard state when switching exercise types
    setUserFretboardAnswers([]);
    setRootNotePosition(null);
    setHighlightedFretPositions([]);
    setFretboardInteractive(true); // Always enable interactive mode for quiz
    
    // Reset to First Octave when switching away from interval recognition
    if (exerciseType === 'intervalRecognition' && newType !== 'intervalRecognition' && setFretRange) {
      setFretRange({ start: 0, end: 12 });
    }
  };

  // Enable interactive mode when component mounts
  useEffect(() => {
    console.log('FretboardQuiz mounted - setting fretboardInteractive to true');
    setFretboardInteractive(true);
    return () => {
      // Clean up when unmounting
      setFretboardInteractive(false);
      setUserFretboardAnswers([]);
      setRootNotePosition(null);
      setHighlightedFretPositions([]);
      
      // Reset to First Octave when leaving the quiz entirely
      if (setFretRange) {
        setFretRange({ start: 0, end: 12 });
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  const accuracy = score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0;
  const selectedExercise = exerciseOptions.find(ex => ex.value === exerciseType);

  return (
    <Card>
      {!testMode && (
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Brain className="h-5 w-5" />
              Fretboard Quiz
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant={accuracy >= 70 ? "default" : "secondary"}>
                {score.correct}/{score.total} ({accuracy}%)
              </Badge>
              <Button variant="ghost" size="sm" onClick={resetScore}>
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
      )}
      <CardContent className="space-y-6">
        {/* Exercise Type Selection - Hidden in test mode */}
        {!testMode && (
          <Card className="p-4">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">Choose Exercise Type</h4>
              <Select value={exerciseType} onValueChange={handleExerciseTypeChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {exerciseOptions.map((option) => (
                    <SelectItem 
                      key={option.value} 
                      value={option.value}
                      disabled={!option.available}
                    >
                      <div className="flex items-center gap-2">
                        <option.icon className="h-4 w-4" />
                        <div className="flex-1">
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs text-muted-foreground">
                            {option.description}
                          </div>
                        </div>
                        {!option.available && (
                          <Badge variant="secondary" className="text-xs">
                            Standard tuning only
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Card>
        )}

        {/* Instructions - Hidden in test mode */}
        {!testMode && (
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {selectedExercise && <selectedExercise.icon className="h-4 w-4 text-blue-600" />}
                <span className="text-sm font-medium text-blue-900">
                  {selectedExercise?.label}
                </span>
              </div>
              <p className="text-sm text-blue-800">
                {selectedExercise?.description}. Click on the fretboard to answer the questions.
              </p>
              {exerciseType === 'chordRecognition' && !isStandardTuning && (
                <p className="text-sm text-amber-800 bg-amber-50 p-2 rounded">
                  ⚠️ Chord recognition is only available in standard tunings. 
                  Switch to standard tuning in the left panel to enable this exercise.
                </p>
              )}
            </div>
          </Card>
        )}

        {/* Exercise Components */}
        {exerciseType === 'noteRecognition' && (
          <NoteRecognitionQuiz
            tuning={tuning}
            fretRange={fretRange}
            userFretboardAnswers={userFretboardAnswers}
            setUserFretboardAnswers={setUserFretboardAnswers}
            setHighlightedFretPositions={setHighlightedFretPositions}
            correctUserFretboardAnswers={correctUserFretboardAnswers}
            setCorrectUserFretboardAnswers={setCorrectUserFretboardAnswers}
            incorrectUserFretboardAnswers={incorrectUserFretboardAnswers}
            setIncorrectUserFretboardAnswers={setIncorrectUserFretboardAnswers}
            updateScore={updateScore}
            currentScore={score}
            isMobile={isMobile}
            onQuizStateChange={onQuizStateChange}
          />
        )}

        {exerciseType === 'chordRecognition' && isStandardTuning && (
          <ChordRecognitionQuiz
            tuning={tuning}
            fretRange={fretRange}
            userFretboardAnswers={userFretboardAnswers}
            setUserFretboardAnswers={setUserFretboardAnswers}
            setHighlightedFretPositions={setHighlightedFretPositions}
            setRootNotePosition={setRootNotePosition}
            updateScore={updateScore}
            currentScore={score}
            currentKey={currentKey}
          />
        )}

        {exerciseType === 'intervalRecognition' && (
          <IntervalRecognitionQuiz
            tuning={tuning}
            fretRange={fretRange}
            setFretRange={setFretRange}
            userFretboardAnswers={userFretboardAnswers}
            setUserFretboardAnswers={setUserFretboardAnswers}
            setHighlightedFretPositions={setHighlightedFretPositions}
            correctUserFretboardAnswers={correctUserFretboardAnswers}
            setCorrectUserFretboardAnswers={setCorrectUserFretboardAnswers}
            incorrectUserFretboardAnswers={incorrectUserFretboardAnswers}
            setIncorrectUserFretboardAnswers={setIncorrectUserFretboardAnswers}
            setRootNotePosition={setRootNotePosition}
            updateScore={updateScore}
            currentScore={score}
          />
        )}

        {/* Progress Encouragement */}
        {score.total > 0 && score.total % 5 === 0 && (
          <Card className="p-4 bg-green-50 border-green-200">
            <div className="text-center">
              <p className="text-green-800 font-medium">
                Great progress! You've completed {score.total} questions.
              </p>
              <p className="text-sm text-green-600 mt-1">
                {score.streak > 0 && `🔥 ${score.streak} streak! `}
                {accuracy >= 80 && "Excellent accuracy! "}
                {accuracy >= 70 && accuracy < 80 && "Good work! "}
                {accuracy < 70 && "Keep practicing - you're improving! "}
              </p>
            </div>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};

export default FretboardQuiz;